import React, { useEffect, useRef, useState } from "react";

// Lazy-load A-Frame from CDN once. A-Frame registers custom elements like <a-scene> and
// <a-videosphere> that give us an immersive 360º viewer with drag-to-look built in.
let aframeReady = null;
function ensureAframe() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.AFRAME) return Promise.resolve();
  if (aframeReady) return aframeReady;
  aframeReady = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://aframe.io/releases/1.5.0/aframe.min.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { aframeReady = null; reject(new Error("A-Frame failed to load")); };
    document.head.appendChild(s);
  });
  return aframeReady;
}

export default function Video360Player({ src, onError }) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ensureAframe()
      .then(() => { if (!cancelled) setReady(true); })
      .catch(() => { if (!cancelled) { setFailed(true); onError?.(); } });
    return () => { cancelled = true; };
  }, [onError]);

  if (failed) {
    return (
      <div className="w-full h-[70vh] flex items-center justify-center text-white/80 bg-black rounded-lg">
        No se pudo cargar el visor 360º
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="w-full h-[70vh] flex items-center justify-center text-white/70 bg-black rounded-lg">
        Cargando visor 360º…
      </div>
    );
  }

  // Use dangerouslySetInnerHTML so A-Frame owns its own DOM subtree. React re-renders
  // otherwise fight A-Frame's runtime and break the scene.
  // Escape the src to prevent HTML injection even though src comes from our own API.
  const escapedSrc = String(src).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const html = `
    <a-scene embedded style="width:100%;height:70vh;background:#000;" vr-mode-ui="enabled: false" loading-screen="enabled: false">
      <a-assets>
        <video id="v360" src="${escapedSrc}" crossorigin="anonymous" playsinline autoplay loop preload="auto"></video>
      </a-assets>
      <a-videosphere src="#v360" rotation="0 -90 0"></a-videosphere>
      <a-camera position="0 0 0" wasd-controls-enabled="false" look-controls="reverseMouseDrag: true; touchEnabled: true"></a-camera>
    </a-scene>
  `;
  return (
    <div className="w-full max-w-[92vw] max-h-[80vh] rounded-lg overflow-hidden" data-testid="video-360-player" ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />
  );
}
